import axios from 'axios';
import { request } from '@src/utils/request';

let boundsDataController = null;

const fetchJsonWithProgress = async (url, { signal, onProgress } = {}) => {
    if (!url) return [];

    const res = await fetch(url, { signal });
    const reader = res.body.getReader();
    const contentLength = Number(res.headers.get('content-length'));
    let receivedLength = 0;
    const chunks = [];

    let readResult = await reader.read();
    while (!readResult.done) {
        const { value } = readResult;
        chunks.push(value);
        receivedLength += value.length;
        if (contentLength > 0 && onProgress) {
            onProgress((receivedLength / contentLength) * 100);
        }
        readResult = await reader.read();
    }

    const chunkAll = new Uint8Array(receivedLength);
    let position = 0;
    for (const chunk of chunks) {
        chunkAll.set(chunk, position);
        position += chunk.length;
    }

    return JSON.parse(new TextDecoder('utf-8').decode(chunkAll));
};

// ---------------------------- 地块数据（citymap / OSS） ----------------------------

// POST /api/map/citymap/initMapData - 初始化地图数据
export const postMapCitymapInitMapData = data =>
    request({
        url: '/api/map/citymap/initMapData',
        method: 'post',
        data,
    });

// POST /api/map/citymap/allSimpleList - 获取地块数据
export const postMapCitymapAllSimpleList = data => {
    if (boundsDataController) {
        boundsDataController.abort();
        boundsDataController = null;
    }
    boundsDataController = new AbortController();
    return request({
        url: '/api/map/citymap/allSimpleList',
        method: 'post',
        data,
        signal: boundsDataController.signal,
    });
};

// 拉取 OSS 地块 JSON（带下载进度）
export const fetchMapBoundsJson = ({ url, onProgress } = {}) =>
    fetchJsonWithProgress(url, {
        signal: boundsDataController?.signal,
        onProgress,
    });

// ---------------------------- 街道边界（system/geo） ----------------------------

// POST /api/system/geo/selectStreetGeoPolygonList - 获取街道 Geo 数据
export const postSystemGeoSelectStreetGeoPolygonList = params =>
    request({
        url: '/api/system/geo/selectStreetGeoPolygonList',
        method: 'post',
        params,
    });

// ---------------------------- 地图渲染配置（surfaceBase） ----------------------------

// GET /api/mydraw/surfaceBase/list - 获取默认渲染配置
export const getMydrawSurfaceBaseList = params =>
    request({
        url: '/api/mydraw/surfaceBase/list',
        method: 'get',
        params,
    });

// ---------------------------- 地铁线路与站点 ----------------------------

// GET /api/map/citymap/subways - 地铁线路
export const getMapCitymapSubways = params =>
    request({
        url: '/api/map/citymap/subways',
        method: 'get',
        params,
    });

// GET /api/map/citymap/subwayStations - 地铁站点
export const getMapCitymapSubwayStations = params =>
    request({
        url: '/api/map/citymap/subwayStations',
        method: 'get',
        params,
    });

// ---------------------------- 商圈版本与 SURFACE 平铺数据 ----------------------------

/**
 * GET /api/mydraw/map/list
 * 调用：`useMapVersion.init()` 首页初始化时，与已发布版本列表并行拉取（每会话一次）。
 * 返回：`data[]` 用户自定义地图（`id`、`name`、`isRecycle`、`isDefaultFlag` 等）；`useMapVersion` 过滤
 *       `isRecycle === '0'` 后写入 `mapVersion` store 的「我的商圈」（type: '1'）选项。
 */
export const getMydrawMapList = ({ areaId, mapId, name = '' }) =>
    request({
        url: '/api/mydraw/map/list',
        method: 'get',
        params: { areaId, mapId, name },
    });

/**
 * GET /api/version/getAlreadyPublishVersion
 * 调用：`useMapVersion.init()` 首页初始化时，与自定义地图列表并行拉取（每会话一次）。
 * 返回：`data[]` 已发布商圈版本（`version`、`versionName`、`isDefaultFlag` 等）；写入 store 的
 *       「标准商圈」（type: '2'）选项，供 `SurfaceVersionPanel` 展示与切换。
 */
export const getVersionAlreadyPublishVersion = ({ areaId, mapId }) =>
    request({
        url: '/api/version/getAlreadyPublishVersion',
        method: 'get',
        params: { areaId, mapId },
    });

/**
 * POST /api/version/previewByVersion
 * 调用：`loadSurfaceTiledData()` 在 mapVersion store 选中 type `'2'`（已发布版本）且版本仍存在于列表时。
 * 入参：`{ areaId, version, surfaceType: 'SURFACE', previewType: 'USER' }`。
 * 返回：`data.ICON` → 静态贴图列表；`data.AREA_SURFACE` → 商圈 MultiPolygon / MultiLabel 源数据。
 */
export const postVersionPreviewByVersion = data =>
    request({
        url: '/api/version/previewByVersion',
        method: 'post',
        data,
    });

/**
 * GET /api/mydraw/iconUse/list
 * 调用：`loadSurfaceTiledData()` — 未选版本或 type `'1'` 自定义地图时拉 SURFACE 贴图。
 * 入参：`surfaceType`、`areaId`、`mapId`；自定义地图版本额外传 `drawMapId`。
 * 返回：图标数组（文字 ImageGround / 固定图标源数据，含 `httpUrl`、`bounds`、`rotate` 等）。
 */
export const getMydrawIconUseList = ({ drawMapId, surfaceType, areaId, mapId }) =>
    request({
        url: '/api/mydraw/iconUse/list',
        method: 'get',
        params: {
            surfaceType,
            areaId,
            mapId,
            ...(drawMapId ? { drawMapId } : {}),
        },
    });

/**
 * GET /api/mydraw/plate/listByCondition
 * 调用：`loadSurfaceTiledData()` 在 mapVersion store 选中 type `'1'`（自定义地图）时，与 icon 并行拉取。
 * 入参：函数入参 `customMapId` 对应 query `drawMapId`，以及 `areaId`、`surfaceType: 'SURFACE'`。
 * 返回：商圈多边形列表（`polygon`、`centroidGeo`、`name`、`fillColor` 等），供 MultiPolygon / MultiLabel。
 */
export const getMydrawPlateListByCondition = ({ customMapId, areaId, surfaceType }) =>
    request({
        url: '/api/mydraw/plate/listByCondition',
        method: 'get',
        params: { drawMapId: customMapId, areaId, surfaceType },
    });

/**
 * GET /api/map/citymap/getAreaPolypon
 * 调用：`loadSurfaceTiledData()` 官方默认分支、`loadSurfaceHoverData()`（未开通会员悬浮模式），`deep: 3` 拉商圈面。
 * 入参：`{ map: currentCity, deep: 3 }`（deep 2 为区域，lite 不使用）。
 * 返回：`{ code, data[] }` 商圈边界与名称字段，与自定义地图 polygon 结构一致。
 */
export const getMapCitymapAreaPolygon = ({ map, deep }) => {
    const mapId = map?.id ?? map?.mapId;
    return request({
        url: '/api/map/citymap/getAreaPolypon',
        method: 'get',
        params: {
            deep,
            areaId: map?.areaId,
            ...(mapId != null && map?.id !== null ? { mapId } : {}),
        },
    });
};

// ---------------------------- 第三方服务 — 天气 ----------------------------

// GET https://apis.map.qq.com/ws/weather/v1 - 获取天气信息
export const getMapWeather = (params = {}) =>
    axios.get('https://apis.map.qq.com/ws/weather/v1', {
        params: {
            key: import.meta.env.VITE_APP_TENCENT_MAP_KEY,
            type: 'now',
            ...params,
        },
    });

// ---------------------------- 项目搜索 ----------------------------

// GET /api/map/citymap/searchByName - 按名称搜索项目
export const searchProjectApi = params =>
    request({
        url: '/api/map/citymap/searchByName',
        method: 'get',
        params,
    });

// ---------------------------- 鼠标样式 ----------------------------

// GET /api/mouse/skin/list - 获取鼠标样式列表
export const getMouseStyleListApi = () =>
    request({
        url: '/api/mouse/skin/list',
    });

// POST /api/mouse/useconfig/editSave - 保存默认鼠标样式
export const saveMouseThemeApi = data =>
    request({
        url: '/api/mouse/useconfig/editSave',
        method: 'post',
        data,
    });

// ---------------------------- 户型数据 ----------------------------

// GET /api/map/modelphoto/listByModelId - 获取户型数据
export const getHouseTypeListApi = params =>
    request({
        url: '/api/map/modelphoto/listByModelId',
        method: 'get',
        params,
    });
